import { useEffect, useRef, useState } from "react";
import {
  getRows,
  upsertAndPrune,
  isTableReplicaAvailable,
} from "@/lib/offline/table-replica-db";

/** Tope de filas cacheadas por tabla/empresa (evita crecimiento sin techo). */
const MAX_CACHED_ROWS = 1000;

/**
 * Espeja las filas que una tabla trae online a IndexedDB y, offline, expone la
 * última copia local. Genérico: lo usa `useTableData`, así toda tabla del ERP
 * gana lecturas offline. Hermano de `useReferenceReplica` (catálogos).
 *
 * @param tableName nombre de la tabla (define el scope en el store)
 * @param liveRows  `queryResult.page` del server (undefined offline/cargando)
 * @param companyId empresa actual (scoping)
 */
export function useTableReplica<T extends { _id: string }>({
  tableName,
  liveRows,
  companyId,
}: {
  tableName: string;
  liveRows: T[] | undefined;
  companyId: string | undefined;
}): { cachedRows: T[]; isHydrated: boolean } {
  const [cachedRows, setCachedRows] = useState<T[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);
  const available = useRef(isTableReplicaAvailable());

  // Hidratar desde IndexedDB al montar / cambiar de tabla o empresa. Da la copia
  // local que se sirve cuando estamos offline.
  useEffect(() => {
    let cancelled = false;
    if (!available.current || !companyId) {
      setIsHydrated(true);
      return;
    }
    (async () => {
      try {
        const rows = await getRows<T>(tableName, companyId);
        if (!cancelled) setCachedRows(rows);
      } catch {
        // Sin réplica disponible: seguimos solo con live.
      } finally {
        if (!cancelled) setIsHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tableName, companyId]);

  // Espejar la página viva (upsert + prune). Solo online (liveRows definido).
  // Best-effort: escribir la caché nunca debe romper el flujo online.
  useEffect(() => {
    if (
      !available.current ||
      !companyId ||
      liveRows === undefined ||
      liveRows.length === 0
    ) {
      return;
    }
    void (async () => {
      try {
        await upsertAndPrune(
          tableName,
          companyId,
          liveRows as Array<{ _id: string } & Record<string, unknown>>,
          MAX_CACHED_ROWS,
        );
      } catch {
        // no-op
      }
    })();
  }, [tableName, companyId, liveRows]);

  return { cachedRows, isHydrated };
}
